/**
 * Translatable toast text for every error code the API contract defines.
 *
 * The server's error `message` is English and written for developers, so the
 * client chooses what a user reads from the error's `code` and fills in values
 * from its typed `data`. `apiErrorLabels` is typed against the contract's error
 * maps, so a new error code fails typecheck until it has a label here.
 */
import type { ErrorMap, InferContractRouterErrorMap, ORPCErrorFromErrorMap } from '@orpc/contract';
import type { AppContract } from '@repo/shared/orpc/contract';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

type ApiErrorMap = InferContractRouterErrorMap<AppContract>;
type DefinedApiError = ApiErrorMap extends infer M ? (M extends ErrorMap ? ORPCErrorFromErrorMap<M> : never) : never;
type ApiErrorCode = DefinedApiError['code'];
type ApiErrorData<C extends ApiErrorCode> = Extract<DefinedApiError, { code: C }>['data'];

type ApiErrorLabel<C extends ApiErrorCode> = {
    label: TLabel;
    params?: (data: ApiErrorData<C>) => FormatParams;
};

/** Shown for a server error whose code the contract doesn't define. */
export const fallbackApiErrorLabel = t('api_error.fallback', 'Something went wrong. Please try again.');

export const networkApiErrorLabel = t('api_error.network', "Couldn't reach the server. Check your connection and try again.");

export const apiErrorLabels: { [C in ApiErrorCode]: ApiErrorLabel<C> } = {
    UNAUTHORIZED: { label: t('api_error.unauthorized', 'Please log in and try again.') },
    FORBIDDEN: { label: t('api_error.forbidden', "You don't have permission to do that.") },
    BAD_REQUEST: { label: t('api_error.bad_request', "That couldn't be saved. Please check it and try again.") },
    NOT_FOUND: { label: t('api_error.not_found', "We couldn't find that. It may have been deleted.") },
    INTERNAL_ERROR: { label: t('api_error.internal_error', 'Something went wrong on our end. Please try again.') },
    SUSPENDED: { label: t('api_error.suspended', 'Your account is suspended.') },
    FEATURE_DISABLED: { label: t('api_error.feature_disabled', 'This feature is turned off.') },
    UNSUPPORTED_LOCALE: { label: t('api_error.unsupported_locale', "This isn't available in your language.") },
    RATE_LIMITED: { label: t('api_error.rate_limited', "You're doing that too often. Please wait a moment and try again.") },
    SPACE_CLOSED: { label: t('api_error.space_closed', 'This conversation is closed to new contributions.') },
    INVALID_CURSOR: { label: t('api_error.invalid_cursor', "Couldn't load more. Please refresh and try again.") },
    ALREADY_ANSWERED: { label: t('api_error.already_answered', "You've already answered here. You can edit your answer instead.") },
    INVALID_ROLE: { label: t('api_error.invalid_role', "That role doesn't exist.") },
    DUPLICATE_TERM: {
        label: t('api_error.duplicate_term', '"{term}" is already on the list.'),
        params: ({ term }) => ({ term }),
    },
    CHANNEL_HAS_SPACES: { label: t('api_error.channel_has_spaces', 'This channel still has conversations. Move them before deleting it.') },
    CANNOT_DELETE_DEFAULT: { label: t('api_error.cannot_delete_default', "The default channel can't be deleted.") },
    MEMBER_NOT_ADMIN: {
        label: t('api_error.member_not_admin', 'Only admins can be added to a channel: {emails}'),
        params: ({ emails }) => ({ emails: emails.join(', ') }),
    },
    SPACE_HAS_COMMENTS: { label: t('api_error.space_has_comments', "The slider's positions can't be renamed once people have commented.") },
    NOT_MINOR_VIOLATION: { label: t('api_error.not_minor_violation', "This comment isn't flagged as a minor violation.") },
    PERSPECTIVE_VIOLATION_NOT_CONFIRMABLE: {
        label: t(
            'api_error.perspective_violation_not_confirmable',
            "This comment was rejected for its perspective, so it can't be confirmed.",
        ),
    },
    REACTIONS_NOT_ENABLED: { label: t('api_error.reactions_not_enabled', 'Reactions are turned off for this conversation.') },
    INVALID_REACTION_TYPE: { label: t('api_error.invalid_reaction_type', "That reaction isn't available in this conversation.") },
    UPVOTES_NOT_ENABLED: { label: t('api_error.upvotes_not_enabled', 'Upvotes are turned off for this conversation.') },
    PIN_LIMIT_REACHED: {
        label: t('api_error.pin_limit_reached', 'You can pin at most {limit} comments here. Unpin one first.'),
        params: ({ limit }) => ({ limit }),
    },
    DUPLICATE_PERSPECTIVE: { label: t('api_error.duplicate_perspective', 'Two perspectives have the same label. Give each one its own.') },
    PERSPECTIVE_TEXT_HAS_EMOJI: { label: t('api_error.perspective_text_has_emoji', "Perspectives can't contain emoji.") },
    PERSPECTIVE_TEXT_INVALID_CHARACTERS: {
        label: t('api_error.perspective_text_invalid_characters', 'A perspective needs at least one letter.'),
    },
    TOO_MANY_WRITE_IN_PERSPECTIVES: {
        label: t('api_error.too_many_write_in_perspectives', 'You can add only one perspective of your own.'),
    },
    UNREGISTERED_COMMENT_PROPERTY: {
        label: t('api_error.unregistered_comment_property', "That comment couldn't be saved. Please refresh and try again."),
    },
    PERSPECTIVE_TEXT_TOO_LONG: {
        label: t('api_error.perspective_text_too_long', 'A perspective can be at most {maxLength} characters.'),
        params: ({ maxLength }) => ({ maxLength }),
    },
    DUPLICATE_POLL_OPTION: {
        label: t('api_error.duplicate_poll_option', '"{pollOption}" is already an option.'),
        params: ({ pollOption }) => ({ pollOption }),
    },
    POLL_NOT_ENABLED: { label: t('api_error.poll_not_enabled', "The poll isn't turned on for this conversation.") },
    INVALID_POLL_OPTION: { label: t('api_error.invalid_poll_option', "That isn't one of the poll's options.") },
    INVALID_NAME: { label: t('api_error.invalid_name', 'That name needs at least one letter or number.') },
    EMPTY_NAME: { label: t('api_error.empty_name', 'Please enter a name.') },
    NAME_TOO_SHORT: { label: t('api_error.name_too_short', 'That name is too short.') },
    NAME_TOO_LONG: { label: t('api_error.name_too_long', 'That name is too long.') },
    NAME_TAKEN: {
        label: t('api_error.name_taken', '"{name}" is already taken. Please choose another name.'),
        params: ({ name }) => ({ name }),
    },
    NAME_PROHIBITED: { label: t('api_error.name_prohibited', "That name isn't allowed. Please choose another.") },
    NAME_NOT_REAL: { label: t('api_error.name_not_real', "Please choose a name that isn't a real person's full name.") },
    SOURCES_UNREACHABLE: { label: t('api_error.sources_unreachable', "Couldn't reach any of the sources. Please try again later.") },
    INVALID_CONVERSATION_KEY: { label: t('api_error.invalid_conversation_key', "That isn't a valid conversation key.") },
    UNKNOWN_SOURCE: { label: t('api_error.unknown_source', "That source isn't one we know.") },
    NOT_SCORED: {
        label: t('api_error.not_scored', '{unscored} of {total} comments have not been scored yet.'),
        params: ({ unscored, total }) => ({ unscored, total }),
    },
    COMMENT_NOT_FOUND: { label: t('api_error.comment_not_found', "We couldn't find that comment. It may have been deleted.") },
    OPENGRAPH_FETCH_FAILED: { label: t('api_error.opengraph_fetch_failed', "Couldn't load a preview for that link.") },
    RELATED_SPACE_LIMIT_REACHED: {
        label: t('api_error.related_space_limit_reached', 'You can link at most {limit} related conversations.'),
        params: ({ limit }) => ({ limit }),
    },
    ORDER_OUT_OF_DATE: { label: t('api_error.order_out_of_date', 'This list changed since you opened it. Please refresh and try again.') },
    DUPLICATE_SPACE: { label: t('api_error.duplicate_space', 'A conversation with that key already exists.') },
    FORMAT_NOT_ENABLED: { label: t('api_error.format_not_enabled', "That format isn't available here.") },
    MODERATION_MODE_NOT_ENABLED: { label: t('api_error.moderation_mode_not_enabled', "That moderation mode isn't available here.") },
    INVALID_STORY_URL: { label: t('api_error.invalid_story_url', "That isn't a valid story link.") },
    DUPLICATE_STORY: { label: t('api_error.duplicate_story', 'That story has already been added.') },
    INVALID_SPACE: { label: t('api_error.invalid_space', "We couldn't find that conversation.") },
    TOO_MANY_POSITIONS: {
        label: t('api_error.too_many_positions', 'A story can show its teaser in at most {maxPositions} places.'),
        params: ({ maxPositions }) => ({ maxPositions }),
    },
    INVALID_VOTE: { label: t('api_error.invalid_vote', "That vote couldn't be counted. Please try again.") },
    NOT_ASSIGNED: { label: t('api_error.not_assigned', "This video voting isn't attached to a story yet.") },
};
